/**
 * Microphone → text via the browser's Web Speech API (no library).
 * Works on iOS Safari (webkitSpeechRecognition) and Android Chrome; both need a
 * secure context (HTTPS) for the mic and send audio to the platform's recogniser.
 * Continuous listening stops by itself on silence on iOS, so we restart it.
 *
 * In the store apps there is no Web Speech API; the phone's own recogniser stands in
 * (`@capacitor-community/speech-recognition`), one utterance per `start()`, so we loop.
 * Every event goes into `log` (mirrored to localStorage 'lunaloo.voiceLog') so a debug
 * session can read what the recogniser did after the fact: `lunaloo.voice.log`.
 */
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

export type VoiceStatus = 'off' | 'listening' | 'unsupported' | 'error';
export interface VoiceEvent {
  /** wall-clock ms */
  t: number;
  k: string;
  v?: string;
}

const LOG_KEY = 'lunaloo.voiceLog';
const LOG_MAX = 80;
/** the Android recogniser is torn down and rebuilt on every start; starting again too soon loses the session */
const SESSION_GAP_MS = 600;

// Minimal typings: lib.dom doesn't ship the webkit-prefixed constructor.
interface RecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface RecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<RecognitionResultLike>;
}
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => RecognitionLike;

function getCtor(): RecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const sleep = (ms: number): Promise<void> => new Promise((res) => setTimeout(res, ms));

export class VoiceInput {
  /** Called with each recognised phrase. `final` is false for in-progress guesses. */
  onTranscript?: (text: string, final: boolean) => void;
  onStatus?: (status: VoiceStatus, detail?: string) => void;
  /** What the recogniser did, oldest first (also in localStorage 'lunaloo.voiceLog'). */
  readonly log: VoiceEvent[] = [];

  private rec: RecognitionLike | null = null;
  private wanted = false;
  private restartTimer = 0;

  constructor() {
    try {
      const saved = JSON.parse(localStorage.getItem(LOG_KEY) ?? '[]') as VoiceEvent[];
      if (Array.isArray(saved)) this.log.push(...saved.slice(-LOG_MAX));
    } catch {
      // unreadable log: start fresh
    }
    this.note('boot', Capacitor.isNativePlatform() ? 'native' : 'web');
  }

  private note(k: string, v?: string): void {
    this.log.push({ t: Date.now(), k, v });
    if (this.log.length > LOG_MAX) this.log.splice(0, this.log.length - LOG_MAX);
    try {
      localStorage.setItem(LOG_KEY, JSON.stringify(this.log));
    } catch {
      // storage full or private mode; the in-memory log still works
    }
  }

  private transcript(text: string, final: boolean): void {
    const t = text.trim().toLowerCase();
    if (!t) return;
    this.note(final ? 'final' : 'partial', t);
    this.onTranscript?.(t, final);
  }

  get supported(): boolean {
    // the store apps have no Web Speech API; the native recogniser stands in
    return Capacitor.isNativePlatform() || (getCtor() !== null && window.isSecureContext);
  }

  get listening(): boolean {
    return this.wanted;
  }

  toggle(): void {
    if (this.wanted) this.stop();
    else this.start();
  }

  start(): void {
    if (Capacitor.isNativePlatform()) {
      void this.startNative();
      return;
    }
    const Ctor = getCtor();
    if (!Ctor || !window.isSecureContext) {
      this.note('unsupported');
      this.onStatus?.('unsupported', window.isSecureContext ? 'no speech recognition here' : 'needs https for the mic');
      return;
    }
    this.wanted = true;
    this.note('start');
    const rec = (this.rec = new Ctor());
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        this.transcript(r[0].transcript, r.isFinal);
      }
    };
    rec.onerror = (e) => {
      this.note('error', e.error);
      // 'no-speech' and 'aborted' are routine; anything else we surface
      if (e.error !== 'no-speech' && e.error !== 'aborted') this.onStatus?.('error', e.error);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') this.wanted = false;
    };
    rec.onend = () => {
      // iOS ends the session after a pause; come back unless the user turned it off
      if (this.wanted) this.restartTimer = window.setTimeout(() => this.safeStart(), 250);
      else this.onStatus?.('off');
    };
    this.safeStart();
  }

  // --- native (Capacitor): the phone's own recogniser. One utterance per `start()`, so loop.
  private nativeListening = false;
  private lastPartial = '';
  private nativeListener: { remove: () => Promise<void> } | null = null;

  private async startNative(): Promise<void> {
    this.wanted = true;
    this.note('start');
    try {
      const { available } = await SpeechRecognition.available();
      if (!available) {
        this.wanted = false;
        this.note('unsupported');
        this.onStatus?.('unsupported', 'no speech recognition on this phone');
        return;
      }
      let perm = await SpeechRecognition.checkPermissions();
      if (perm.speechRecognition !== 'granted') perm = await SpeechRecognition.requestPermissions();
      if (perm.speechRecognition !== 'granted') {
        this.wanted = false;
        this.note('error', 'permission ' + perm.speechRecognition);
        this.onStatus?.('error', 'microphone blocked for Lunaloo');
        return;
      }
      if (!this.nativeListener) {
        this.nativeListener = await SpeechRecognition.addListener('partialResults', (d: { matches?: string[] }) => {
          const t = d.matches?.[0];
          if (t) {
            this.lastPartial = t;
            this.transcript(t, false);
          }
        });
      }
      if (!this.nativeListening) void this.nativeLoop();
    } catch (e) {
      this.wanted = false;
      this.note('error', String((e as Error).message ?? e));
      this.onStatus?.('error', String((e as Error).message ?? e));
    }
  }

  private stopped: (() => void) | null = null;
  /** resolves a pause-while-hidden wait early (visibility change or stop()) */
  private wake: (() => void) | null = null;

  /** The mic is foreground-only on Android: a hidden app records silence. Wait until we're back. */
  private async waitVisible(): Promise<void> {
    if (!document.hidden) return;
    this.note('paused', 'hidden');
    await new Promise<void>((res) => {
      const on = (): void => {
        if (document.hidden && this.wanted) return;
        document.removeEventListener('visibilitychange', on);
        this.wake = null;
        res();
      };
      this.wake = on;
      document.addEventListener('visibilitychange', on);
    });
    this.note('resumed', document.hidden ? 'stopped' : 'visible');
  }

  private async nativeLoop(): Promise<void> {
    this.nativeListening = true;
    // with partial results on, start() returns at once and words arrive as events; the session
    // ends with a listeningState 'stopped' (silence, a result, or an error). Then go again.
    const stateListener = await SpeechRecognition.addListener('listeningState', (d: { status: string }) => {
      this.note('state', d.status);
      if (d.status === 'stopped') this.stopped?.();
    });
    while (this.wanted) {
      await this.waitVisible();
      if (!this.wanted) break;
      this.onStatus?.('listening');
      this.lastPartial = '';
      const began = Date.now();
      try {
        let done = false;
        const ended = new Promise<void>((res) => {
          this.stopped = () => {
            done = true;
            res();
          };
          window.setTimeout(() => {
            done = true;
            res();
          }, 15_000);
        });
        await SpeechRecognition.start({ language: 'en-US', maxResults: 3, partialResults: true, popup: false });
        this.note('session');
        // the recogniser gives up on silence without saying so: poll it, and go again as soon as it's idle
        void (async () => {
          await sleep(700);
          while (!done && this.wanted) {
            try {
              const { listening } = await SpeechRecognition.isListening();
              if (!listening) {
                this.stopped?.();
                return;
              }
            } catch {
              return;
            }
            await sleep(400);
          }
        })();
        await ended;
        this.stopped = null;
        this.note('end', `${((Date.now() - began) / 1000).toFixed(1)}s${this.lastPartial ? ' "' + this.lastPartial + '"' : ''}`);
        if (this.lastPartial) this.transcript(this.lastPartial, true);
      } catch (e) {
        this.note('error', String((e as Error).message ?? e));
        await sleep(500);
      }
      if (this.wanted) await sleep(SESSION_GAP_MS);
    }
    await stateListener.remove();
    this.nativeListening = false;
    this.note('off');
    this.onStatus?.('off');
  }

  private safeStart(): void {
    try {
      this.rec?.start();
      this.onStatus?.('listening');
    } catch {
      // start() throws if already running; ignore
    }
  }

  stop(): void {
    this.wanted = false;
    this.note('stop');
    window.clearTimeout(this.restartTimer);
    if (Capacitor.isNativePlatform()) {
      this.wake?.();
      void SpeechRecognition.stop().catch(() => undefined);
      return;
    }
    this.rec?.stop();
    this.rec = null;
    this.onStatus?.('off');
  }
}
